// src/pages/admin/AdminSaaS.js
// Dashboard SaaS : MRR, ARR, revenus, marchands actifs, packs vendus
// Maxula

import React, { useEffect, useState } from 'react';
import { FiTrendingUp, FiRepeat, FiDollarSign, FiUsers, FiPackage, FiClock } from 'react-icons/fi';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { subscriptionAPI } from '../../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

const Kpi = ({ icon: Icon, label, value, suffix, color, hint }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5">
    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}1a`, color }}><Icon size={16} /></span>
      {label}
    </div>
    <div className="text-3xl font-bold text-gray-900">{value}<span className="text-base font-normal text-gray-400 ml-1">{suffix}</span></div>
    {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
  </div>
);

const AdminSaaS = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await subscriptionAPI.saasStats(); setData(res.data?.data || null); }
      catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
  if (!data) return <div className="p-6 text-gray-400">Données indisponibles.</div>;

  const primary = '#db2777';
  const revData = {
    labels: data.revenueSeries?.labels || [],
    datasets: [{ label: 'Revenu abonnements (MAD)', data: data.revenueSeries?.values || [], borderColor: primary, backgroundColor: 'rgba(219,39,119,0.12)', fill: true, tension: 0.35 }]
  };
  const tiers = data.tierDistribution || [];
  const tierData = {
    labels: tiers.map((t) => t.name),
    datasets: [{ data: tiers.map((t) => t.count), backgroundColor: ['#db2777', '#2563eb', '#7c3aed', '#16a34a', '#d97706'] }]
  };

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Dashboard SaaS</h1>
          <p className="text-gray-500 mb-6">Indicateurs de revenus récurrents et d'activité.</p>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Kpi icon={FiTrendingUp} label="MRR (revenu mensuel récurrent)" value={fmt(data.mrr)} suffix="MAD" color="#db2777" />
        <Kpi icon={FiRepeat} label="ARR (revenu annuel récurrent)" value={fmt(data.arr)} suffix="MAD" color="#2563eb" />
        <Kpi icon={FiDollarSign} label="Revenu total encaissé" value={fmt(data.totalRevenue)} suffix="MAD" color="#16a34a" hint="Factures abonnements payées (TTC)" />
        <Kpi icon={FiUsers} label="Marchands actifs (abonnés)" value={fmt(data.activeMerchants)} suffix={`/ ${fmt(data.totalMerchants)}`} color="#7c3aed" />
        <Kpi icon={FiPackage} label="Packs vendus" value={fmt(data.packsVendus)} color="#0891b2" />
        <Kpi icon={FiClock} label="Demandes en attente" value={fmt(data.pendingSubs)} color="#d97706" hint="À valider dans Abonnements" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-3">Revenu abonnements (6 mois)</h3>
          <div className="h-64"><Line data={revData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} /></div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Répartition des packs actifs</h3>
          {tiers.length ? (
            <div className="h-64 flex items-center justify-center"><Doughnut data={tierData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} /></div>
          ) : (
            <div className="text-center text-gray-400 py-16">Aucun abonnement actif.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSaaS;
